import { Link } from "react-router-dom";
import { ArrowLeft, Envelope, Phone, MapPin } from "@phosphor-icons/react";

/**
 * Terms of Service page — rendered verbatim from the legal text supplied by
 * product. Available at both /terms and /terms-of-service.
 */
export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white" data-testid="terms-of-service-page">
      <div className="max-w-3xl mx-auto px-6 md:px-10 py-10 md:py-16">
        <Link
          to="/"
          className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8"
          data-testid="tos-back-home"
        >
          <ArrowLeft size={16} weight="bold" /> Back
        </Link>

        <h1 className="font-heading text-4xl md:text-5xl font-black tracking-tight">
          Terms of Service
        </h1>
        <p className="text-white/50 text-sm mt-2 mb-10">Last Updated: July 2026</p>

        <div className="prose prose-invert prose-sm md:prose-base max-w-none space-y-6 text-white/85 leading-relaxed">
          <p>
            Welcome to Tainapp. By accessing or using our website, web
            application, and associated services collectively, the Platform,
            you agree to be bound by these Terms of Service. Please read them
            carefully.
          </p>

          <section>
            <h2 className="font-heading text-2xl font-bold text-white mt-8 mb-3">
              1. Description of Service
            </h2>
            <p>
              Tainapp is a digital media platform that provides users access
              to digital tools, software features, and premium content
              published by independent content creators ("Creators").
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-bold text-white mt-8 mb-3">
              2. Pre-Paid Platform Credits
            </h2>
            <h3 className="font-heading text-lg font-bold text-white mt-4 mb-2">
              2.1 Purchase &amp; Usage
            </h3>
            <p>
              To access certain premium features, digital utilities, or
              creator content on the Platform, users may purchase pre-paid
              platform credits ("Credits").
            </p>
            <p>
              Credits represent a non-exclusive, limited, non-transferable,
              and revocable license to access digital services strictly within
              the Platform.
            </p>
            <h3 className="font-heading text-lg font-bold text-white mt-4 mb-2">
              2.2 Monetary Value &amp; Non-Transferability
            </h3>
            <p>
              <strong>No Cash Value:</strong> Credits are purely internal units
              of platform utility and do not constitute real currency,
              electronic money, or stored financial value.
            </p>
            <p>
              Credits cannot be exchanged for cash, refunded, resold,
              transferred, or assigned to other users or third parties outside
              the Platform.
            </p>
            <h3 className="font-heading text-lg font-bold text-white mt-4 mb-2">
              2.3 Pricing &amp; Expiration
            </h3>
            <p>
              Credit bundle pricing is displayed clearly at checkout and is
              subject to change at our discretion.
            </p>
            <p>
              Unused Credits do not expire unless explicitly stated at the
              time of purchase or if your account is terminated in accordance
              with these Terms.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-bold text-white mt-8 mb-3">
              3. Refund Policy
            </h2>
            <p>
              All Credit purchases are final. Because Credits provide
              immediate access to digital content and platform features, all
              purchases are non-refundable, except where required by
              applicable local law or technical billing error verified by our
              support team.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-bold text-white mt-8 mb-3">
              4. Creator Content &amp; Revenue Share
            </h2>
            <h3 className="font-heading text-lg font-bold text-white mt-4 mb-2">
              4.1 Creator Earnings
            </h3>
            <p>
              Creators who publish content or engage the Platform earn a
              percentage-based revenue share ("Creator Royalties").
            </p>
            <p>
              The specific revenue-share percentage and payout thresholds are
              detailed in the Creator Wallet Dashboard.
            </p>
            <h3 className="font-heading text-lg font-bold text-white mt-4 mb-2">
              4.2 Verification &amp; Payouts
            </h3>
            <p>
              Creators must complete basic Identity Verification
              (KYC)—including providing a valid government-issued ID and a
              verified account under their legal name—before receiving
              payouts.
            </p>
            <p>
              Payouts are distributed via licensed third-party payment
              processors to the Creator's registered local bank account or
              mobile money wallet.
            </p>
            <p>
              We reserve the right to withhold or cancel payouts if content
              violates our Community Guidelines or if fraudulent activity is
              detected.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-bold text-white mt-8 mb-3">
              5. User Conduct &amp; Acceptable Use
            </h2>
            <p>You agree not to use the Platform to:</p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Publish, request, or engage in any unlawful, fraudulent, or abusive activity.</li>
              <li>Attempt to bypass, manipulate, or exploit the Credit system or payment mechanisms.</li>
              <li>Upload content that infringes upon third-party intellectual property or privacy rights.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-bold text-white mt-8 mb-3">
              6. Payment Processing
            </h2>
            <p>
              All financial transactions (Credit purchases and Creator
              payouts) are processed securely through licensed third-party
              payment gateways. We do not directly store your credit card or
              payment card PIN details on our servers.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-bold text-white mt-8 mb-3">
              7. Limitation of Liability
            </h2>
            <p>
              To the maximum extent permitted by applicable law, Tainapp
              shall not be liable for any indirect, incidental, special,
              consequential, or punitive damages arising out of or related
              to your use of the Platform or Credits.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-2xl font-bold text-white mt-8 mb-3">
              8. Contact Us
            </h2>
            <p>
              If you have any questions regarding these Terms or the
              Platform, please contact us at:
            </p>
            <div className="mt-4 space-y-3 rounded-xl bg-white/[0.03] border border-white/10 p-5">
              <div className="flex items-center gap-3">
                <Phone size={18} weight="fill" className="text-emerald-400 shrink-0" />
                <a
                  href="tel:+233539025750"
                  className="text-white/85 hover:text-white"
                  data-testid="tos-contact-phone"
                >
                  +233 539 025 750
                </a>
              </div>
              <div className="flex items-center gap-3">
                <Envelope size={18} weight="fill" className="text-emerald-400 shrink-0" />
                <a
                  href="mailto:support@tainapp.com"
                  className="text-white/85 hover:text-white"
                  data-testid="tos-contact-email"
                >
                  support@tainapp.com
                </a>
              </div>
              <div className="flex items-start gap-3">
                <MapPin size={18} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" />
                <span className="text-white/85">
                  Baabs Junction, between Pokuase-Awoshie Road, Amamorley,
                  Accra-Ghana
                </span>
              </div>
            </div>
          </section>
        </div>

        <div className="mt-12 pt-6 border-t border-white/10 text-center text-xs text-white/40">
          © {new Date().getFullYear()} Tainapp. All rights reserved.
        </div>
      </div>
    </div>
  );
}
